import React from 'react';
import { ButtonProps } from '@/types/components';

/**
 * Button Component - Consistent button styling across the application
 * 
 * Variants:
 * - primary: Main action buttons (orange theme)
 * - secondary: Secondary actions (gray theme)
 * - outline: Outlined buttons
 * - ghost: Text-only buttons
 * - danger: Destructive actions (red theme)
 * 
 * Sizes:
 * - sm: 32px height, smaller padding
 * - md: 40px height, default padding
 * - lg: 48px height, larger padding
 */

interface ExtendedButtonProps extends ButtonProps {
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

const Button: React.FC<ExtendedButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  className = '',
  icon,
  iconPosition = 'left',
  ...props
}) => {
  const baseClasses = [
    'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
    'select-none'
  ];

  const variantClasses = {
    primary: [
      'bg-orange-500 text-white border border-orange-500',
      'hover:bg-orange-600 hover:border-orange-600',
      'focus:ring-orange-500',
      'active:bg-orange-700'
    ],
    secondary: [
      'bg-gray-100 text-gray-700 border border-gray-300',
      'hover:bg-gray-200 hover:border-gray-400',
      'focus:ring-gray-500',
      'active:bg-gray-300'
    ],
    outline: [
      'bg-transparent text-orange-500 border border-orange-500',
      'hover:bg-orange-50 hover:border-orange-600',
      'focus:ring-orange-500',
      'active:bg-orange-100'
    ],
    ghost: [
      'bg-transparent text-gray-700 border border-transparent',
      'hover:bg-gray-100',
      'focus:ring-gray-500',
      'active:bg-gray-200'
    ],
    danger: [
      'bg-red-500 text-white border border-red-500',
      'hover:bg-red-600 hover:border-red-600',
      'focus:ring-red-500',
      'active:bg-red-700'
    ]
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm min-h-[32px] gap-1.5',
    md: 'px-4 py-2 text-sm min-h-[40px] gap-2',
    lg: 'px-6 py-3 text-base min-h-[48px] gap-2.5'
  };

  const combinedClasses = [
    ...baseClasses,
    ...variantClasses[variant],
    sizeClasses[size],
    className
  ].join(' ');

  const LoadingSpinner = () => (
    <div className="animate-spin rounded-full border-2 border-current border-t-transparent w-4 h-4" />
  );

  const content = (
    <>
      {loading && <LoadingSpinner />}
      {!loading && icon && iconPosition === 'left' && icon}
      {!loading && children}
      {!loading && icon && iconPosition === 'right' && icon}
    </>
  );

  return (
    <button
      type={type}
      className={combinedClasses}
      onClick={onClick}
      disabled={disabled || loading}
      {...props}
    >
      {content}
    </button>
  );
};

export default Button;
